import React, { useEffect, useState } from "react";
import WebmasterMedicSidebar from "../../components/WebmasterMedicSidebar";
import { Link, useSearchParams } from "react-router-dom";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import search from "../../assets/images/search.svg";
import arrowLeft from "../../assets/images/arrow-left.svg";
import arrowRight from "../../assets/images/arrow-right.svg";
import Swal from "sweetalert2";
import ToggleSwitch from "../../components/ToggleSwitch";

interface Patient {
  id: string;
  firstname: string;
  lastname: string;
  cnp?: string | null;
  type?: string | null;
  das?: string | null;
  age_range?: string | null;
  category?: string | null;
  parentFirstName?: string | null;
  parentLastName?: string | null;
  parentEmail?: string | null;
  parentPhone?: string | null;
  country?: string | null;
  zone?: string | null;
  city?: string | null;
  is_active: boolean;
  profile_status: string; // "lead" | "full"
}

interface PagedResponse {
  items: Patient[];
  total_count: number;
  page: number;
  page_size: number;
}

interface PatientAgeRange {
  key: string;
  value: string;
}
interface PatientType {
  key: string;
  value: string;
}
interface PatientCategory {
  key: string;
  value: string;
}

const MedicPatientsPage: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [patientAgeRanges, setPatientAgeRanges] = useState<PatientAgeRange[]>([]);
  const [patientTypes, setPatientTypes] = useState<PatientType[]>([]);
  const [patientCategories, setPatientCategories] = useState<PatientCategory[]>([]);

  const { token } = useAuth();

  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState<number>(initialPage);

  const pageSize = 20;
  const [totalCount, setTotalCount] = useState<number>(0);
  const totalPages = Math.ceil(totalCount / pageSize);

  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const getVisiblePages = (tp: number): (number | string)[] => {
    if (tp <= 6) return Array.from({ length: tp }, (_, i) => i + 1);
    return [1, 2, 3, "...", tp - 2, tp - 1, tp];
  };

  useEffect(() => {
    const fetchPatients = async () => {
      if (!token) return;

      setLoading(true);
      try {
        if (searchTerm.length >= 3) {
          const response = await fetch(
            `${API_BASE_URL}/patient/search?query=${encodeURIComponent(searchTerm)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const result = await response.json();
          setPatients(result.data || []);
          setTotalCount(result.data ? result.data.length : 0);
        } else {
          const response = await fetch(
            `${API_BASE_URL}/patient/paged?page=${currentPage}&page_size=${pageSize}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const data: PagedResponse = await response.json();
          setPatients(data.items || []);
          setTotalCount(data.total_count || 0);
          setSearchParams({ page: String(currentPage) });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, [currentPage, setSearchParams, searchTerm, token]);

  useEffect(() => {
    const fetchConstants = async () => {
      if (!token) return;

      try {
        const [ageRangesResponse, typesResponse, categoriesResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/types/age-ranges`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/patient-types`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/types/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const [ageRangeData, patientTypeData, categoryData] = await Promise.all([
          ageRangesResponse.json(),
          typesResponse.json(),
          categoriesResponse.json(),
        ]);

        setPatientAgeRanges(ageRangeData.data || []);
        setPatientTypes(patientTypeData.data || []);
        setPatientCategories(categoryData.data || []);
      } catch (error) {
        console.error("Eroare la încărcarea datelor:", error);
      }
    };

    fetchConstants();
  }, [token]);

  const handleToggleActivePatient = async (patientId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/patient/${patientId}/toggle-active`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Nu s-a putut schimba starea pacientului.");

      const data = await response.json();
      setPatients((prev) => prev.map((p) => (p.id === patientId ? { ...p, is_active: data.is_active } : p)));
    } catch (error) {
      console.error(error);
      Swal.fire("Eroare!", "Nu s-a putut modifica starea pacientului.", "error");
    }
  };

  const handleDelete = async (patientId: string) => {
    const result = await Swal.fire({
      title: "Ești sigur?",
      text: "Vrei să ștergi acest pacient?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6C298B",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Da, șterge-l!",
      cancelButtonText: "Anulează",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/patient/${patientId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Eroare la ștergerea pacientului.");

      setPatients((prev) => prev.filter((p) => p.id !== patientId));
      Swal.fire("Șters!", "Pacientul a fost șters.", "success");
    } catch (error) {
      console.error(error);
      Swal.fire("Eroare!", "Nu s-a putut șterge pacientul.", "error");
    }
  };

  const getPatientAgeRangeValue = (key?: string | null) => {
    if (!key) return "";
    return patientAgeRanges.find((i) => i.key === key)?.value || key;
  };
  const getPatientTypeValue = (key?: string | null) => {
    if (!key) return "";
    return patientTypes.find((i) => i.key === key)?.value || key;
  };
  const getPatientCategoryValue = (key?: string | null) => {
    if (!key) return "";
    return patientCategories.find((i) => i.key === key)?.value || key;
  };

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsedMedic") === "true";
  });

  const [showInactive, setShowInactive] = useState<boolean>(() => {
    return localStorage.getItem("showInactivePatients") === "true";
  });
  useEffect(() => {
    localStorage.setItem("showInactivePatients", String(showInactive));
  }, [showInactive]);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSearchOpen(false);
  };

  const displayedPatients = showInactive ? patients : patients.filter((t) => t.is_active);

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterMedicSidebar onCollapseChange={(collapsed) => setIsCollapsed(collapsed)} />

      <div className="page-content">
        <h1 className="page-title">Adaugă și Editează Pacienți</h1>

        <div className="table-top">
          <div className="page-subtitle">Pacienți</div>

          <div className="table-actions">
            <div id="patientsSearch" className="search-container">
              {searchOpen ? (
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Caută pacient..."
                  autoFocus
                />
              ) : (
                <img src={search} alt="search" onClick={() => setSearchOpen(true)} style={{ cursor: "pointer" }} />
              )}
              {searchOpen && (
                <button onClick={clearSearch} className="clear-search">
                  x
                </button>
              )}
            </div>

            <ToggleSwitch checked={showInactive} onChange={setShowInactive} labelOn="Arată inactivi" labelOff="Arată inactivi" />

            <Link to="/webmaster-medic/add-patient">
              <button className="primary-btn">Adaugă Pacient</button>
            </Link>
          </div>
        </div>

        {loading ? (
          <p>Se încarcă...</p>
        ) : displayedPatients.length === 0 ? (
          <p>Nu există pacienți înregistrați.</p>
        ) : (
          <div className="table-rounded-container">
            <table className="table-rounded">
              <thead>
                <tr>
                  <th>Nume / Prenume</th>
                  <th>CNP</th>
                  <th>Tip</th>
                  <th>DAS</th>
                  <th>Vârstă</th>
                  <th>Categorie</th>
                  <th>Status</th>
                  <th className="column-actions">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {displayedPatients.map((p) => (
                  <tr key={p.id}>
                    <td>
                      {p.lastname} {p.firstname}{" "}
                      {p.profile_status === "lead" && (
                        <span style={{ fontSize: 12, opacity: 0.7 }}>(consult)</span>
                      )}
                    </td>
                    <td>{p.cnp || "-"}</td>
                    <td>{p.profile_status === "lead" ? "-" : getPatientTypeValue(p.type)}</td>
                    <td>{p.das || "-"}</td>
                    <td>{p.profile_status === "lead" ? "-" : getPatientAgeRangeValue(p.age_range)}</td>
                    <td>{p.profile_status === "lead" ? "-" : getPatientCategoryValue(p.category)}</td>
                    <td>
                      <button
                        onClick={() => handleToggleActivePatient(p.id)}
                        className={p.is_active ? "active" : "inactive"}
                      >
                        {p.is_active ? "Activ" : "Inactiv"} <span className="button-dot">&#x2022;</span>
                      </button>
                    </td>
                    <td className="column-actions">
                      <button onClick={() => handleDelete(p.id)}>Șterge</button>
                      <Link to={`/webmaster-medic/edit-patient/${p.id}`}>
                        <button>Editare</button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {searchTerm.length < 3 && (
              <div className="pagination">
                <button disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>
                  <img src={arrowLeft} alt="inapoi" className="pagination-left" /> Înapoi
                </button>

                <div className="pages">
                  {getVisiblePages(totalPages).map((pageItem, index) => {
                    if (pageItem === "...") {
                      return (
                        <span key={index} className="pagination-ellipsis">
                          {pageItem}
                        </span>
                      );
                    }
                    return (
                      <button
                        key={index}
                        className={currentPage === pageItem ? "active-page" : ""}
                        onClick={() => goToPage(Number(pageItem))}
                      >
                        {pageItem}
                      </button>
                    );
                  })}
                </div>

                <button disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>
                  Înainte <img src={arrowRight} alt="Înainte" className="pagination-right" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicPatientsPage;